import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarSrc } from "@/lib/defaults";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { audienceOf } from "@/lib/postAudience";

// Lien d'une story qu'on ne peut pas voir : compte privé non suivi (lot 523) ou audience restreinte par l'auteur
// (lot 651, `author.reason === "audience"`). Écran plein, façon visionneuse, sans rien révéler.
export const StoryPrivateScreen = ({ author, onClose }) => {
  const { t } = useApp();
  const nav = useNavigate();
  if (!author) return null;
  const first = (author.name || "").split(" ")[0] || author.name || "";
  const restricted = author.reason === "audience";
  const aud = restricted ? audienceOf(author.audience) : null;
  return (
    <div className="fixed inset-0 z-[70] bg-black text-white grid place-items-center px-6" onClick={onClose} data-testid="story-private-screen" data-reason={restricted ? "audience" : "private"}>
      <button type="button" onClick={onClose} aria-label={t("common.close", "Fermer")}
        className="absolute right-4 h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        style={{ top: "calc(var(--sa-top, 0px) + 0.75rem)" }} data-testid="story-private-close">
        <X size={20} />
      </button>
      <div className="max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
        <div className="relative mx-auto w-fit">
          <Avatar className="h-24 w-24 border-4 border-white/20">
            <AvatarImage src={avatarSrc(author)} />
            <AvatarFallback className="text-2xl text-black">{author.name?.[0]}</AvatarFallback>
          </Avatar>
          <span className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full grid place-items-center border-4 border-black" style={{ background: "var(--yalola-primary)" }}>
            {aud ? <aud.icon size={16} /> : <Lock size={16} />}
          </span>
        </div>
        <div className="mt-5 text-lg font-bold font-display flex items-center justify-center gap-1.5" data-testid="story-private-author">{author.name}<VerifiedBadge user={author} size={16} /></div>
        <div className="mt-1 text-sm text-white/70 inline-flex items-center gap-1.5">
          {aud ? <><aud.icon size={13} /> {t("story.audience_badge", "Audience : {label}").replace("{label}", t(aud.tkey, aud.label))}</> : <><Lock size={13} /> {t("profile.private_badge", "Compte privé")}</>}
        </div>
        <h2 className="mt-6 text-xl font-bold font-display" data-testid="story-private-title">
          {restricted ? t("story.audience_locked_title", "Story réservée") : t("profile.private_locked", "Ce compte est privé")}
        </h2>
        <p className="mt-2 text-sm text-white/75">
          {restricted
            ? t("story.audience_locked_hint", "{name} a réservé cette story à un cercle restreint (abonnés, suivis ou {name} seul·e). Tu n'en fais pas partie pour l'instant.").replaceAll("{name}", first)
            : t("story.private_link_hint", "Ses stories sont réservées à ses abonnés. Suis {name} : ta demande devra être acceptée.").replace("{name}", first)}
        </p>
        <button type="button" onClick={() => { onClose?.(); nav(`/profile/${author.id}`); }}
          className="mt-6 w-full h-12 rounded-full font-semibold text-white transition-transform active:scale-95"
          style={{ background: "var(--yalola-primary)" }} data-testid="story-private-profile">
          {t("post.private_open_profile", "Voir le profil")}
        </button>
      </div>
    </div>
  );
};
